import type { GetHealthNotesRequest, HealthNote } from "@Shared/schemas/notes/noteSchemas";
import { GetHealthNotesResponseSchema } from "@Shared/schemas/notes/noteSchemas";
import { HEALTH_AND_SAFETY_SESSION_KEY } from "@Helpers/clear-auth-session-storage";
import { useCallback, useEffect, useRef, useState } from "react";

export const HEALTH_NOTES_REQUEST_TIMEOUT_MS = 10_000;

export interface IHealthNotesStartup {
  locale: string;
  enabled?: boolean;
  loadNotes: (request: GetHealthNotesRequest) => Promise<unknown>;
}

export const useHealthNotes = ({ locale, enabled = true, loadNotes }: IHealthNotesStartup) => {
  const [queue, setQueue] = useState<HealthNote[]>([]);
  const started = useRef(false);
  const shouldShow = useRef<boolean | null>(null);
  const pending = useRef<{ locale: string; response: Promise<unknown> } | null>(null);
  const loadNotesRef = useRef(loadNotes);
  loadNotesRef.current = loadNotes;

  useEffect(() => {
    if (!enabled) return;
    if (shouldShow.current === null) {
      try {
        shouldShow.current = window.sessionStorage.getItem(HEALTH_AND_SAFETY_SESSION_KEY) !== "true";
      } catch {
        shouldShow.current = true;
      }
    }
    if (!shouldShow.current || started.current) return;

    let active = true;
    if (pending.current?.locale !== locale) {
      const load = loadNotesRef.current;
      pending.current = {
        locale,
        response: Promise.resolve().then(() => load({ locale, app_type: "vr" })),
      };
    }

    const timeout = setTimeout(() => {
      active = false;
      started.current = true;
    }, HEALTH_NOTES_REQUEST_TIMEOUT_MS);

    void pending.current.response.then(
      response => {
        if (!active) return;
        clearTimeout(timeout);
        started.current = true;
        if (!GetHealthNotesResponseSchema.is(response) || response.notes.length === 0) return;
        try {
          window.sessionStorage.setItem(HEALTH_AND_SAFETY_SESSION_KEY, "true");
        } catch {
          // Storage may be unavailable; the current mount still displays only once.
        }
        setQueue(response.notes);
      },
      () => {
        if (!active) return;
        clearTimeout(timeout);
        started.current = true;
      }
    );

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [locale, enabled]);

  const advance = useCallback((noteId: number) => {
    setQueue(current => (current[0]?.id === noteId ? current.slice(1) : current));
  }, []);

  return { note: queue[0] ?? null, advance };
};
